import React, { 
    useState, 
    useEffect,
    useRef,         // 27 Jul 2026
 } from 'react'
import useTitle from '@/hooks/useTitle'
import { useNavigate } from 'react-router'
import { addIcon, userProfileIcon2 } from '@/assets/img/imgAssets'
import {
  PlusIcon,
  MinusIcon,
  TrashIcon,
  PencilSquareIcon,
  // 27 Jul 2026
  ChevronLeftIcon,
  ChevronRightIcon,
  EyeIcon,
  EyeSlashIcon,
} from "@heroicons/react/24/outline"
import { UserGroupIcon, BuildingOffice2Icon, ArrowTrendingUpIcon, CalendarDaysIcon, ArrowRightCircleIcon } from '@heroicons/react/24/solid'
import PulseLoader from "react-spinners/PulseLoader"
import { useGetCoachUserPadsQuery } from './coachUserPadsApiSlice'
import { useGetUserPicQuery } from "../users/usersApiSlice"

import EtCoachOrgTrailBoardCompProd from '../coachOrgTrailBoards/EtCoachOrgTrailBoardCompProd'


const EtCoachUserPadListCompProd = ({coachUserId, coachOrgId}) => {
    // First Created - 21 Oct 2024
    // Author - Atul Pandey
     // Inherited from EtCoachUserPadList
    // Purpose - to filter and show coach user pads for a specific user
    // Updated - 03 Mar 2025 - copied from EtCoachUserPadListComp and refined it for prod (userMagicHome --> etapp)
    // Updated - 08 Mar 2025 - sessionSequence fix - showing sessions in ascending order of sessionSequence
    // Updated - 03 Jul 2026 - Trail Board refinements
    // Updated - 27 Jul 2026 - tab reorganization for orgs and hide / display org names (privacy)

    // console.log('Inside CoachUserPadListCompProd......')
    
    // 0 - Set Page Title
    useTitle('Coach User Pad List')

    // 1 - set page navigations
    const navigate = useNavigate()
   
    // 2 - Initialize showDetails state as an object where key is the session index
    const [showCoachDetails, setShowCoachDetails] = useState(true)
    const [showOrgs, setShowOrgs] = useState({})
    const [showTrails, setShowTrails] = useState({})
    const [showDetails, setShowDetails] = useState({})

    // 03 Jul 2026 - Track the selected tab for each coach organization trail.
    // Default display will be Overview once the Trail Board is added.
    const [activeTrailTab, setActiveTrailTab] = useState({})

    // 27 Jul 2026 - Organization tab navigation
    const [selectedOrgByCoach, setSelectedOrgByCoach] = useState({})

    // 27 Jul 2026 - Organization name visibility by organization
    const [visibleOrganizationNames, setVisibleOrganizationNames] = useState({})

    // 27 Jul 2026 - Organization carousel references by coach
    const orgTabsRefs = useRef({})

    // 3 - Extract the coach user pads data
    const { data: coachUserPadsData, isError, error, isLoading, refetch } = useGetCoachUserPadsQuery()
    const coachUserPads = coachUserPadsData?.ids.map((id) => {
        const coachUserPad = coachUserPadsData?.entities[id]
        return coachUserPad
    })?.filter(pad => pad.coachId?._id === coachUserId )
    // console.log('Coach User Pads Data', coachUserPadsData)
    // console.log('Coach User Pads:', coachUserPads)

    // 3a - Refetch data when the component is mounted or navigated to
    useEffect(() => {
        refetch();
    }, [navigate, refetch]);


    // 4 - Group the data by coachId, organization, trails, sessions, and users
    const groupedData = coachUserPads?.reduce((acc, pad) => {
        const coachId = pad.coachId?.name || 'No Coach';
        const coachUsername = pad.coachId?.username || 'No Username';
        const orgId = pad.orgId?._id || 'No org id';
        const orgName = pad.userId.userOrgId?.organizationName || 'No Organization';
        const trailId=pad.trailId?._id || 'No Trail Id';
        const trailName = pad.trailId?.trailName || 'No Trail';
        const trailPic = pad.trailId?.pic || 'No Trail';
        const sessionId = pad.sessionId?._id || 'No Session';

        const session = {
            sessionId: sessionId,
            sessionName: pad.sessionId?.sessionName || 'No Session',
            sessionPic: pad.sessionId?.pic || '',
            sessionDesc: pad.sessionId?.sessionDesc || '',
            sessionSequence: pad.sessionId?.sessionSequence || 0, // ✅ 08 Mar 2025 - Added sessionSequence
            users: []
        };

        const user = {
            userId: pad.userId?._id,
            userName: pad.userId?.name,
            username: pad.userId?.username,
            userEmail: pad.userId?.email,
            ...pad // Other properties from pad if necessary
        };
        // console.log('user', user)

        if (!acc[coachId]) acc[coachId] = { username: coachUsername, orgs: {} };                                                                                                             
        if (!acc[coachId].orgs[orgId]) acc[coachId].orgs[orgId] = { name: orgName, trails: {} };
        if (!acc[coachId].orgs[orgId].trails[trailName]) acc[coachId].orgs[orgId].trails[trailName] = { id:trailId,pic: trailPic, sessions: {} }; // Updated
        if (!acc[coachId].orgs[orgId].trails[trailName].sessions[sessionId]) acc[coachId].orgs[orgId].trails[trailName].sessions[sessionId] = { ...session }; // Updated
        
        // 21 Oct 2024 - added the if logic to filter out the sponsor from the users list
        if (user.userId._id!== coachUserId) {
            // console.log('user.userId._id', user.userId._id)
            // console.log('coachId', coachId)
            // console.log('user.userId._id !== coachId ?', user.userId._id !== coachId)

        acc[coachId].orgs[orgId].trails[trailName].sessions[sessionId].users.push(user);
        }
        
        return acc;
    }, {});

    // console.log('Grouped Data:', groupedData);

    // 27 Jul 2026 - Select the first available organization for each coach
    useEffect(() => {
        if (!groupedData) return

        setSelectedOrgByCoach((prev) => {
            const next = { ...prev }

            Object.entries(groupedData).forEach(([coachName, coachData]) => {
                const organizationIds = Object.keys(coachData?.orgs || {})
                const currentSelectedOrgId = next[coachName]

                if (
                    organizationIds.length > 0 &&
                    !organizationIds.includes(currentSelectedOrgId)
                ) {
                    next[coachName] = organizationIds[0]
                }
            })

            return next
        })
    }, [coachUserPadsData])

    // 5 - Toggle Show function for coach user pad details
    const handleToggleDetails = (trailIndex, sessionIndex) => {
        const key = `${trailIndex}-${sessionIndex}`;
        setShowDetails(prev => ({ ...prev, [key]: !prev[key] }));
    };

    // 5a - Toggle Show function for trails
    const handleToggleCoachDetails = (coachId) => {
        setShowCoachDetails(prev => ({ ...prev, [coachId]: !prev[coachId] }));
    }
    
    // 5b - Toggle Show function for trails
    const handleToggleTrails = (orgIndex, trailIndex) => {
        const key = `${orgIndex}-${trailIndex}`;
        setShowTrails(prev => ({ ...prev, [key]: !prev[key] }));
    };

    // 5c - Toggle Show function for trails
    const handleToggleOrgs = (coachIndex, orgIndex) => {
        const key = `${coachIndex}-${orgIndex}`;
        setShowOrgs(prev => ({ ...prev, [key]: !prev[key] }));
    };

    // 5d - 03 Jul 2026 - Switch one organization trail between Overview and Sessions.
    const handleTrailTabChange = (orgId, trailId, tabName) => {
        setActiveTrailTab((prev) => ({
            ...prev,
            [orgId]: {
                ...prev[orgId],
                [trailId]: tabName,
            },
        }))
    }

    // 5e - 27 Jul 2026 - Scroll organization tabs left or right
    const handleScrollOrgTabs = (coachKey, direction) => {
        const carousel = orgTabsRefs.current[coachKey]

        if (!carousel) return

        const scrollDistance = Math.max(carousel.clientWidth * 0.7, 240)

        carousel.scrollBy({
            left: direction === 'left'
                ? -scrollDistance
                : scrollDistance,
            behavior: 'smooth',
        })
    }

    // 5f - 27 Jul 2026 - Mask organization names while retaining a stable width
    // const getDisplayedOrgName = (orgName) => {
    //     if (showOrganizationNames) return orgName

    //     const visibleLength = Math.max(
    //         Math.min(orgName?.length || 8, 18),
    //         8
    //     )

    //     return '•'.repeat(visibleLength)
    // }

    // 27 Jul 2026 - Create an abbreviated masked organization name
    const getOrgAbbreviation = (orgName = '') => {
        const normalizedName = orgName.trim()

        if (!normalizedName) return 'Org***'

        const nameParts = normalizedName
            .split(/\s+/)
            .filter(Boolean)

        if (nameParts.length === 1) {
            const abbreviation = nameParts[0]
                .slice(0, 2)
                .replace(/[^a-zA-Z0-9]/g, '')

            if (!abbreviation) return 'Org***'

            return `${
                abbreviation.charAt(0).toUpperCase() +
                abbreviation.slice(1).toLowerCase()
            }***`
        }

        const abbreviation = nameParts
            .map((part) => part.charAt(0))
            .join('')
            .toUpperCase()

        return `${abbreviation || 'Org'}***`
    }

    // 27 Jul 2026 - Return full or abbreviated organization name
    const getDisplayedOrgName = (orgId, orgName) =>
        visibleOrganizationNames[orgId]
            ? orgName
            : getOrgAbbreviation(orgName)
    
    // 27 Jul 2026 - Show or hide one organization name
    const handleToggleOrganizationName = (event, orgId) => {
        event.stopPropagation()

        setVisibleOrganizationNames((prev) => ({
            ...prev,
            [orgId]: !prev[orgId],
        }))
    }
    
    if (isLoading) return <PulseLoader color={"#ea580c"} />
    if (isError) return <p className="text-red-500">{error?.data?.message}</p>

    return (
        <div className='bg-gray-200 m-1 p-1 rounded font-poppins mx-auto'>
            {/* Header */}
            {/* <div className='flex justify-start items-center m-1 p-1'>
                <div><p className='text-2xl m-1 p-1 font-bold'>Et Coach User Pads</p></div>
                <div>
                    <img
                        src={addIcon}
                        alt=""
                        className="h-10 w-10 cursor-pointer"
                        onClick={() => navigate('/coachUserPad/create')}
                    />
                </div>
            </div> */}

           
            {Object.keys(groupedData)?.length === 0 ? (
                <div className="m-1 p-1 bg-white rounded">
                    <p className='text-orange-600 text-xs m-1 p-1'>No coach user pads available. Please create one by clicking the "+" button above.</p>
                </div>
            ) : (
            Object.entries(groupedData)?.map(([coachId, orgs], coachIndex) => {
                const { username: coachUsername, orgs: organizations } = orgs;
                // 27 Jul 2026
                const organizationEntries = Object.entries(organizations)
                const selectedOrgId =
                    selectedOrgByCoach[coachId] || organizationEntries?.[0]?.[0]
    
                const orgCount = Object.entries(organizations)?.length
                const trailCount = Object.values(organizations).reduce((acc, org) => acc + Object.keys(org.trails).length, 0);
                const sessionCount = Object.values(organizations).reduce((acc, org) => {
                    return acc + Object.values(org.trails).reduce((trailAcc, trail) => trailAcc + (trail?.sessions ? Object.keys(trail.sessions).length : 0), 0); // Updated
                }, 0);
                
                const uniqueUserIds = new Set(); // 05 Aug 2024
                Object.values(organizations).forEach(org => {
                    Object.values(org.trails).forEach(trail => {
                        Object.values(trail.sessions || {}).forEach(session => {
                            session.users.forEach(user => uniqueUserIds.add(user.userId._id));
                        });
                    });
                });
                
                const userCount = uniqueUserIds.size; // 05 Aug 2024
                return(
                <div key={coachIndex} className='m-1 p-1 bg-white rounded'>
                    <div className=' '>
                        {/* Coach Header */}
                        {/* <div className='grid grid-cols-11 justify-between items-center m-1 p-1'>
                            <div className='col-span-3 flex justify-start items-center m-1 p-1'>
                                <div onClick={() => handleToggleCoachDetails(coachIndex)} className="mx-2 rounded-full h-7 w-7 hover:bg-white flex justify-center items-center cursor-pointer">{showCoachDetails[coachIndex] ? <MinusIcon className="h-5 w-5 text-orange-600" /> : <PlusIcon className="h-5 w-5 text-orange-600" />}</div>
                                <div>
                                    <User
                                        username={coachUsername}
                                        defaultUserPic={userProfileIcon2}
                                    />
                                </div>
                                <div>
                                    <p className="px-2 mx-2 text-gray-600 font-bold">{coachId}</p>
                                    <p className='mx-2 text-[10px] text-gray-400 rounded-full inline-block px-2 border bg-white shadow shadow-gray-400'>Founder, <span className='text-orange-600'>Eternity Solutions</span></p>
                                </div>
                            </div>
                            <div className='col-span-2 flex justify-start items-center'>
                                <div><p className='text-[10px] text-gray-500 mx-2 px-2'>Organizations :</p></div>
                                <div>
                                            <BuildingOffice2Icon className='h-5 w-5 text-orange-600 m-1'/>
                                </div>
                                <div className='rounded-full flex justify-center items-center h-5 w-5 border border-orange-300 bg-gray-50'><p className='text-orange-600 text-[10px]'>{orgCount}</p></div>
                            </div>
                            <div className='col-span-2 flex justify-start items-center'>
                                <div><p className='text-[10px] text-gray-500 mx-2 px-2'>Trails :</p></div>
                                <div>
                                            <ArrowTrendingUpIcon className='h-5 w-5 text-orange-600 m-1'/>
                                </div>
                                <div className='rounded-full flex justify-center items-center h-5 w-5 border border-orange-300 bg-gray-50'><p className='text-orange-600 text-[10px]'>{trailCount}</p></div>
                            </div>
                            <div className='col-span-2 flex justify-start items-center'>
                                <div><p className='text-[10px] text-gray-500 mx-2 px-2'>Sessions :</p></div>
                                <div>
                                            <CalendarDaysIcon className='h-5 w-5 text-orange-600 m-1'/>
                                </div>
                                <div className='rounded-full flex justify-center items-center h-5 w-5 border border-orange-300 bg-gray-50'><p className='text-orange-600 text-[10px]'>{sessionCount}</p></div>
                            </div>
                            <div className='col-span-2 flex justify-start items-center'>
                                <div><p className='text-[10px] text-gray-500 mx-2 px-2'>Users :</p></div>
                                <div>
                                            <UserGroupIcon className='h-5 w-5 text-orange-600 m-1'/>
                                </div>
                                <div className='rounded-full flex justify-center items-center h-5 w-5 border border-orange-300 bg-gray-50'><p className='text-orange-600 text-[10px]'>{userCount}</p></div>
                            </div>
                        </div> */}

                    {/* 27 Jul 2026 - Organization tabs */}
                    <div className="m-1 rounded border border-gray-200 bg-gray-50 p-1">
                        <div className="flex items-center gap-1">
                            <button
                                type="button"
                                title="Previous organizations"
                                aria-label="Scroll to previous organizations"
                                onClick={() => handleScrollOrgTabs(coachId, 'left')}
                                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-500 shadow-sm transition hover:border-orange-300 hover:bg-orange-50 hover:text-orange-600"
                            >
                                <ChevronLeftIcon className="h-5 w-5" />
                            </button>

                            <div
                                ref={(element) => {
                                    orgTabsRefs.current[coachId] = element
                                }}
                                role="tablist"
                                aria-label="Coach organizations"
                                className="flex min-w-0 flex-1 items-center gap-1 overflow-x-auto scroll-smooth whitespace-nowrap p-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
                            >
                                {organizationEntries.map(([orgId, orgData]) => {
                                    const isSelected = selectedOrgId === orgId

                                    return (
                                        <div
                                            key={orgId}
                                            role="tab"
                                            aria-selected={isSelected}
                                            className={`flex shrink-0 items-center overflow-hidden rounded transition ${
                                                isSelected
                                                    ? 'bg-orange-600 text-white shadow-sm'
                                                    : 'border border-gray-200 bg-white text-gray-600 hover:border-orange-300 hover:bg-orange-50 hover:text-orange-600'
                                            }`}
                                        >
                                            <button
                                                type="button"
                                                title={
                                                    visibleOrganizationNames[orgId]
                                                        ? orgData?.name
                                                        : getOrgAbbreviation(orgData?.name)
                                                }
                                                onClick={() =>
                                                    setSelectedOrgByCoach((prev) => ({
                                                        ...prev,
                                                        [coachId]: orgId,
                                                    }))
                                                }
                                                className="px-4 py-2 text-xs font-semibold"
                                            >
                                                {getDisplayedOrgName(orgId, orgData?.name)}
                                            </button>

                                            <button
                                                type="button"
                                                title={
                                                    visibleOrganizationNames[orgId]
                                                        ? 'Hide organization name'
                                                        : 'Show organization name'
                                                }
                                                aria-label={
                                                    visibleOrganizationNames[orgId]
                                                        ? `Hide ${orgData?.name}`
                                                        : 'Show organization name'
                                                }
                                                aria-pressed={Boolean(visibleOrganizationNames[orgId])}
                                                onClick={(event) =>
                                                    handleToggleOrganizationName(event, orgId)
                                                }
                                                className={`mr-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-full transition ${
                                                    isSelected
                                                        ? 'text-white hover:bg-orange-500'
                                                        : 'text-orange-600 hover:bg-orange-100'
                                                }`}
                                            >
                                                {visibleOrganizationNames[orgId] ? (
                                                    <EyeSlashIcon className="h-4 w-4" />
                                                ) : (
                                                    <EyeIcon className="h-4 w-4" />
                                                )}
                                            </button>
                                        </div>
                                    )
                                })}
                            </div>

                            <button
                                type="button"
                                title="Next organizations"
                                aria-label="Scroll to next organizations"
                                onClick={() => handleScrollOrgTabs(coachId, 'right')}
                                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-500 shadow-sm transition hover:border-orange-300 hover:bg-orange-50 hover:text-orange-600"
                            >
                                <ChevronRightIcon className="h-5 w-5" />
                            </button>

                        </div>
                    </div>

                    

                    {/* Coach Details */}
                    {/* {showCoachDetails && Object.entries(organizations)?.map(([orgId, orgData], orgIndex) => { */}
                    {/* 27 Jul 2026 */}
                    {showCoachDetails &&
                            organizationEntries
                                .filter(([orgId]) => orgId === selectedOrgId)
                                .map(([orgId, orgData], orgIndex) => {
                        const { name: orgName, trails } = orgData; // Extract orgName and trails
                        const trailCount = Object.keys(trails)?.length;
                        const sessionCount = Object.values(trails).reduce((acc, trail) => acc + (trail?.sessions ? Object.keys(trail.sessions).length : 0), 0); // Ensure sessions is defined
                        const uniqueUserIds = new Set(); // 05 Aug 2024
                        Object.values(trails).forEach(trail => {
                            Object.values(trail?.sessions).forEach(session => {
                                session.users.forEach(user => uniqueUserIds.add(user.userId._id));
                            });
                        });
                        const userCount = uniqueUserIds.size;// 05 Aug 2024
                        return(
                        <div key={orgIndex} className="">
                            {/* Org Header */}
                            <div className='bg-gray-300 rounded m-1 p-1'>
                                <div className='grid grid-cols-10 gap-1 justify-start items-center m-1 p-1'>
                                    <div className='col-span-3 flex justify-start items-center m-1 p-1'>

                                         {/* 27 Jul 2026 - Commented as we are tabbing orgs */}

                                        {/* <div onClick={() => handleToggleOrgs(coachIndex, orgIndex)} className="mx-2 rounded-full h-6 w-6 hover:bg-white flex justify-center items-center cursor-pointer">{showOrgs[`${coachIndex}-${orgIndex}`] ? <MinusIcon className="h-4 w-4 text-orange-600" /> : <PlusIcon className="h-4 w-4 text-orange-600" />}</div> */}

                                        <div><p className='text-[10px] text-gray-500 mx-1 px-1'>Org:</p></div>
                                        <div>
                                            <BuildingOffice2Icon className='h-5 w-5 text-orange-600 m-1'/>
                                        </div>

                                        {/* 27 Jul 2026 */}
                                        <div className="flex min-w-0 items-center gap-1">
                                            <p
                                                className="truncate font-semibold"
                                                title={
                                                    visibleOrganizationNames[orgId]
                                                        ? orgName
                                                        : getOrgAbbreviation(orgName)
                                                }
                                            >
                                                {getDisplayedOrgName(orgId, orgName)}
                                            </p>

                                            <button
                                                type="button"
                                                title={
                                                    visibleOrganizationNames[orgId]
                                                        ? 'Hide organization name'
                                                        : 'Show organization name'
                                                }
                                                aria-label={
                                                    visibleOrganizationNames[orgId]
                                                        ? `Hide ${orgName}`
                                                        : 'Show organization name'
                                                }
                                                aria-pressed={Boolean(visibleOrganizationNames[orgId])}
                                                onClick={(event) =>
                                                    handleToggleOrganizationName(event, orgId)
                                                }
                                                className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-orange-600 hover:bg-white"
                                            >
                                                {visibleOrganizationNames[orgId] ? (
                                                    <EyeSlashIcon className="h-4 w-4" />
                                                ) : (
                                                    <EyeIcon className="h-4 w-4" />
                                                )}
                                            </button>
                                        </div>
                                        {/* <div><p className="font-semibold">{orgName}</p></div> */}
                                    </div>
                                    <div className='col-span-2 flex justify-start items-center m-1 p-1'>
                                        <div><p className='text-[10px] text-gray-500 mx-2 px-2'>Trails :</p></div>
                                        <div>
                                            <ArrowTrendingUpIcon className='h-5 w-5 text-orange-600 m-1'/>
                                        </div>
                                        <div className='rounded-full flex justify-center items-center h-5 w-5 border border-orange-300 bg-gray-50'><p className='text-orange-600 text-[10px]'>{trailCount}</p></div>
                                    </div>
                                    <div className='col-span-2 flex justify-start items-center m-1 p-1'>
                                        <div><p className='text-[10px] text-gray-500 mx-2 px-2'>Sessions :</p></div>
                                        <div>
                                            <CalendarDaysIcon className='h-5 w-5 text-orange-600 m-1'/>
                                        </div>
                                        <div className='rounded-full flex justify-center items-center h-5 w-5 border border-orange-300 bg-gray-50'><p className='text-orange-600 text-[10px]'>{sessionCount}</p></div>
                                    </div>
                                    <div className='col-span-2 flex justify-start items-center m-1 p-1'>
                                        <div><p className='text-[10px] text-gray-500 mx-2 px-2'>Users :</p></div>
                                        <div>
                                            <UserGroupIcon className='h-5 w-5 text-orange-600 m-1'/>
                                        </div>
                                        <div className='rounded-full flex justify-center items-center h-5 w-5 border border-orange-300 bg-gray-50'><p className='text-orange-600 text-[10px]'>{userCount}</p></div>
                                    </div>
                                </div>
                                
                                {/* Org Details */}
                                {/* {showOrgs[`${coachIndex}-${orgIndex}`] && Object.entries(trails)?.map(([trailName, trailData], trailIndex) => { */}

                                {/* 27 Jul 2026 */}
                                {Object.entries(trails)?.map(([trailName, trailData], trailIndex) => {

                                    // 03 Jul 2026
                                    const trailId = trailData?.id

                                    const sessionCount = trailData.sessions 
                                    ? Object.keys(trailData?.sessions).length 
                                    : 0; // Ensure sessions is defined

                                    const uniqueUserIds = new Set(); // 05 Aug 2024

                                    // 03 Jul 2026
                                    const selectedTrailTab = activeTrailTab[orgId]?.[trailId] || 'overview'
                                    
                                    Object.values(trailData.sessions || {}).forEach(session => {
                                        session?.users.forEach(user => uniqueUserIds.add(user.userId._id));
                                    });
                                    const userCount = uniqueUserIds.size; // 05 Aug 2024
                                    
                                    return(
                                    <div key={trailIndex} className='bg-gray-100 rounded m-1 p-1'>
                                        {/* Trail Header */}
                                        <div className='grid grid-cols-9 justify-between items-center m-1 p-1'>

                                            
                                            <div className='col-span-3 flex justify-start items-center m-1 p-1'>

                                               
                                                <div onClick={() => handleToggleTrails(orgIndex, trailIndex)} className="mx-2 rounded-full h-6 w-6 hover:bg-white flex justify-center items-center cursor-pointer">{showTrails[`${orgIndex}-${trailIndex}`] ? <MinusIcon className="h-4 w-4 text-orange-600" /> : <PlusIcon className="h-4 w-4 text-orange-600" />}</div>
                                                
                                                <div className='mx-2 flex justify-center items-center rounded-full bg-gray-100 hover:bg-white h-8 w-8'>
                                                    <img src={trailData.pic} alt="" className='h-6 w-6' />
                                                </div>
                                                <div><p className="font-semibold">{trailName}</p></div>
                                            </div>
                                            <div className='col-span-2 flex justify-start items-center m-1 p-1'>
                                                <div><p className='text-[10px] text-gray-500 mx-2 px-2'>Sessions :</p></div>
                                                <div>
                                                    <CalendarDaysIcon className='h-5 w-5 text-orange-600 m-1'/>
                                                </div>
                                                <div className='rounded-full flex justify-center items-center h-5 w-5 border border-orange-300 bg-gray-50'><p className='text-orange-600 text-[10px]'>{sessionCount}</p></div>
                                            </div>
                                            <div className='col-span-2 flex justify-start items-center m-1 p-1'>
                                                <div><p className='text-[10px] text-gray-500 mx-2 px-2'>Users :</p></div>
                                                <div>
                                                    <UserGroupIcon className='h-5 w-5 text-orange-600 m-1'/>
                                                </div>
                                                <div className='rounded-full flex justify-center items-center h-5 w-5 border border-orange-300 bg-gray-50'><p className='text-orange-600 text-[10px]'>{userCount}</p></div>
                                            </div>
                                        </div>

                                        
                                        {/* 03 Jul 2026 - Coach Trail Board / Session Pad tabs */}
                                        {showTrails[`${orgIndex}-${trailIndex}`] && (
                                        <div className="m-1 flex items-end gap-1 border-b border-gray-300 bg-white px-2 pt-2">
                                            <button
                                            type="button"
                                            onClick={() =>
                                                handleTrailTabChange(orgId, trailId, 'overview')
                                            }
                                            className={`rounded-t px-3 py-2 text-xs font-semibold transition ${
                                                selectedTrailTab === 'overview'
                                                ? 'border-b-2 border-orange-600 bg-orange-50 text-orange-700'
                                                : 'text-gray-500 hover:bg-gray-100'
                                            }`}
                                            >
                                            Overview
                                            </button>

                                            <button
                                            type="button"
                                            onClick={() =>
                                                handleTrailTabChange(orgId, trailId, 'sessions')
                                            }
                                            className={`rounded-t px-3 py-2 text-xs font-semibold transition ${
                                                selectedTrailTab === 'sessions'
                                                ? 'border-b-2 border-orange-600 bg-orange-50 text-orange-700'
                                                : 'text-gray-500 hover:bg-gray-100'
                                            }`}
                                            >
                                            Sessions
                                            </button>
                                        </div>
                                        )}
                                        

                                        
                                        {/* 03 Jul 2026 - Temporary Coach Trail Board placeholder */}
                                        {showTrails[`${orgIndex}-${trailIndex}`] &&
                                        selectedTrailTab === 'overview' && (
                                            <EtCoachOrgTrailBoardCompProd
                                                coachId={coachUserId}
                                                orgId={orgId}
                                                trailId={trailId}
                                                //27 Jul 2026
                                                orgName={getDisplayedOrgName(orgName)}
                                                // orgName={orgName}
                                                trailName={trailName}
                                                trailPic={trailData?.pic}
                                            />
                                        )}
                                        
                                        {showTrails[`${orgIndex}-${trailIndex}`] && 
                                            selectedTrailTab === 'sessions' &&              // 03 Jul 2026
                                            Object.entries(trailData.sessions || {})
                                        .sort(([, a], [, b]) => a.sessionSequence - b.sessionSequence) // ✅ 08 Mar 2025 Sorting by sessionSequence
                                        ?.map(([sessionId, sessionData], sessionIndex) => {                                       
                                            const userCount = sessionData.users ? sessionData.users.length : 0; //08 Aug 2024
                                            // const trailId = trailData.id // 03 Jul 2026
                                            
                                            return(
                                            <div key={sessionId+sessionIndex} className="m-1 p-1 bg-gray-200 rounded">

                                                <div className='grid grid-cols-12 justify-between items-center m-1 p-1'>
                                                    <div className='col-span-6 flex justify-start items-center'>
                                                        <div onClick={() => handleToggleDetails(trailIndex, sessionIndex)} className="mx-2 rounded-full h-6 w-6 hover:bg-white flex justify-center items-center cursor-pointer">{showDetails[`${trailIndex}-${sessionIndex}`] ? <MinusIcon className="h-4 w-4 text-orange-600" /> : <PlusIcon className="h-4 w-4 text-orange-600" />}</div>
                                                        <div className='mx-2 flex justify-center items-center rounded-full bg-gray-100 hover:bg-white h-8 w-8'>
                                                            <img src={sessionData?.sessionPic} alt="" className='h-6 w-6' />
                                                        </div>
                                                        {/* <div><p className="text-xs font-semibold">Session {sessionIndex +1} - {sessionData?.sessionName}</p></div> */}
                                                         <div><p className="text-xs font-semibold">Session {sessionData?.sessionSequence} - {sessionData?.sessionName}</p></div>{/* 08 Mar 2025  */}
                                                    </div>
                                                    <div className='col-span-2 flex justify-start items-center m-1 p-1'>
                                                        <div><p className='text-[10px] text-gray-500 mx-2 px-2'>Users :</p></div>
                                                        <div>
                                                            <UserGroupIcon className='h-5 w-5 text-orange-600 m-1'/>
                                                        </div>
                                                        <div className='rounded-full flex justify-center items-center h-5 w-5 border border-orange-300 bg-gray-50'><p className='text-orange-600 text-[10px]'>{userCount}</p></div>
                                                    </div>
                                                    <div className='col-span-4 flex justify-end items-center mx-2'>
                                                        
                                                        <div className='flex justify-start items-center border border-white cursor-pointer shadow rounded p-1'
                                                        // onClick={() => navigate(`/userMagicHome/coach/update/${coachUserId}/${orgId}/${trailId}/${sessionId}`)}
                                                        onClick={() => navigate(`/etapp/coach/update/${coachUserId}/${orgId}/${trailId}/${sessionId}`)}
                                                        >
                                                            <div><p className='mx-1 px-1 text-[10px] text-gray-400'>Go to session</p></div>
                                                            <div className='rounded-full flex justify-center items-center h-6 w-6 hover:bg-transparent'>
                                                            
                                                            <ArrowRightCircleIcon  className="h-6 w-6 text-lime-600 cursor-pointer" />
                                                        </div>
                                                        </div>

                                                        {/* <div className='rounded-full flex justify-center items-center h-6 w-6 hover:bg-white'>
                                                            <TrashIcon onClick={() => navigate(`/coachUserPad/delete/${orgId}/${trailId}/${sessionId}`)} className="h-4 w-4 text-orange-600 cursor-pointer" />
                                                        </div> */}
                                                    </div>
                                                </div>

                                                <div className="m-1 p-1">
                                                    {showDetails[`${trailIndex}-${sessionIndex}`] && sessionData.users?.map((user, userIndex) => {
                                                        
                                                        return(
                                                        <div key={userIndex} className='m-1 p-1 bg-gray-50 rounded'>
                                                            <div className='flex justify-between items-center m-1 p-1'>
                                                                <div className=' flex justify-start items-center m-1 p-1'>
                                                                    <div className='mx-1 px-1'>
                                                                        <User
                                                                        username={user.username}
                                                                        defaultUserPic={userProfileIcon2}
                                                                    />
                                                                    </div>
                                                                    <div className='mx-1 px-1'>
                                                                        <p className='text-xs text-gray-600 font-bold'>{user.userName}</p>
                                                                        <p className='text-[10px] text-gray-400'>{user.userEmail}</p>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )})}
                                                </div>
                                            </div>
                                        )})}
                                    </div>
                                )})}
                            </div>
                        </div>
                    )})}
                    
                    </div>

                </div>
            )}))}
        </div>
    )
}

const User = ({ username, defaultUserPic }) => {
    const { data: userPic, isFetching: isFetchingUserPic } =
      useGetUserPicQuery(username)
  
    return (
      <div className="flex justify-center items-center h-12 w-12 rounded-full border border-white bg-gradient-to-r from-orange-600 to-orange-400 shadow-lg shadow-black cursor-pointer">
        {/* Display user picture or loading indicator*/}
        {isFetchingUserPic ? (
          <div>Loading...</div>
        ) : userPic ? (
          <img src={userPic} alt="User" className="h-11 w-11  rounded-full" />
        ) : (
          <img src={defaultUserPic} alt="User" className="h-10 w-10 p-1" />
        )}
      </div>
    )
  }


export default EtCoachUserPadListCompProd